import React, {
  createContext, useContext, useState, ReactNode, useMemo, useRef, useEffect,
} from 'react';

interface GlobalSpinnerContextType {
  isVisible: boolean;
  text?: string;
  show: (spinnerText?: string) => void;
  hide: () => void;
}

const GlobalSpinnerContext = createContext<GlobalSpinnerContextType | undefined>(undefined);

interface GlobalSpinnerProviderProps {
  children: ReactNode;
}

export const GlobalSpinnerProvider: React.FC<GlobalSpinnerProviderProps> = ({ children }) => {
  const [isVisible, setIsVisible] = useState(false);
  const [text, setText] = useState<string | undefined>(undefined);
  const activeRequestsRef = useRef(0);
  const showTimeoutIdRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const isVisibleRef = useRef(false);
  const autoHideTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const MAX_VISIBLE_MS = 15000; // safety watchdog

  const show = (spinnerText?: string) => {
    activeRequestsRef.current += 1;
    if (spinnerText !== undefined) {
      setText(spinnerText);
    }

    if (isVisibleRef.current) {
      return;
    }

    if (showTimeoutIdRef.current) {
      return;
    }

    showTimeoutIdRef.current = setTimeout(() => {
      showTimeoutIdRef.current = null;
      if (activeRequestsRef.current > 0) {
        setIsVisible(true);
      }
    }, 500);
  };

  const hide = () => {
    activeRequestsRef.current = Math.max(0, activeRequestsRef.current - 1);

    if (activeRequestsRef.current === 0) {
      if (showTimeoutIdRef.current) {
        clearTimeout(showTimeoutIdRef.current);
        showTimeoutIdRef.current = null;
      }
      if (autoHideTimeoutRef.current) {
        clearTimeout(autoHideTimeoutRef.current);
        autoHideTimeoutRef.current = null;
      }
      setIsVisible(false);
      setText(undefined);
    }
  };

  useEffect(() => () => {
    if (showTimeoutIdRef.current) {
      clearTimeout(showTimeoutIdRef.current);
      showTimeoutIdRef.current = null;
    }
    if (autoHideTimeoutRef.current) {
      clearTimeout(autoHideTimeoutRef.current);
      autoHideTimeoutRef.current = null;
    }
    activeRequestsRef.current = 0;
  }, []);

  const value: GlobalSpinnerContextType = useMemo(() => ({
    isVisible,
    text,
    show,
    hide,
  }), [isVisible, text]);

  // Track visibility ref and set/reset watchdog
  useEffect(() => {
    isVisibleRef.current = isVisible;
    if (isVisible) {
      if (autoHideTimeoutRef.current) {
        clearTimeout(autoHideTimeoutRef.current);
      }
      autoHideTimeoutRef.current = setTimeout(() => {
        // Watchdog to recover from unbalanced show/hide
        activeRequestsRef.current = 0;
        if (showTimeoutIdRef.current) {
          clearTimeout(showTimeoutIdRef.current);
        }
        showTimeoutIdRef.current = null;
        setIsVisible(false);
        setText(undefined);
        if (process.env.NODE_ENV !== 'production') {
          // eslint-disable-next-line no-console
          console.warn('[GlobalSpinner] Auto-hid due to timeout; possible unbalanced show/hide');
        }
      }, MAX_VISIBLE_MS);
    } else if (autoHideTimeoutRef.current) {
      clearTimeout(autoHideTimeoutRef.current);
      autoHideTimeoutRef.current = null;
    }
  }, [isVisible]);

  return (
    <GlobalSpinnerContext.Provider value={value}>
      {children}
    </GlobalSpinnerContext.Provider>
  );
};

export const useGlobalSpinnerContext = () => {
  const context = useContext(GlobalSpinnerContext);
  if (context === undefined) {
    throw new Error('useGlobalSpinnerContext must be used within a GlobalSpinnerProvider');
  }
  return context;
};
